/**
 * Back-key chain (docs/04 §2): handlers are consulted from the highest priority down
 * (modal → screen → root). The first handler returning `true` consumes the key.
 * Pure module (no React) so it can be unit tested and used from the key map.
 */
export type BackHandler = () => boolean | void;

export const BackPriority = {
  /** App root: pops the router stack or shows the exit dialog. */
  root: 0,
  /** Screen-level behaviour (e.g. player cancels auto-advance). */
  screen: 50,
  /** Non-trapping overlays that Back should close first (the expanded rail, docs/04 §7.1). */
  overlay: 75,
  /** Dialogs, popovers, on-screen keyboard modes. */
  modal: 100,
} as const;
export type BackPriorityLevel = (typeof BackPriority)[keyof typeof BackPriority] | number;

interface Entry {
  id: number;
  priority: number;
  fn: BackHandler;
}

let seq = 0;
let entries: Entry[] = [];

/** Registers a handler; returns the unregister function. Later registrations win ties. */
export function registerBackHandler(fn: BackHandler, priority: number = BackPriority.screen) {
  seq += 1;
  const entry: Entry = { id: seq, priority, fn };
  entries = [...entries, entry];
  return () => {
    entries = entries.filter((e) => e !== entry);
  };
}

/** Runs the chain. Returns true when some handler consumed the key. */
export function dispatchBack(): boolean {
  const ordered = [...entries].sort((a, b) => b.priority - a.priority || b.id - a.id);
  for (const entry of ordered) {
    if (entry.fn() === true) return true;
  }
  return false;
}

/** Test helper / hot-reload guard. */
export function resetBackHandlers(): void {
  entries = [];
}

export function backHandlerCount(): number {
  return entries.length;
}
